import { Badge } from "@/components/ui/badge";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { CalendarDays, Eye, Pencil, Star } from "lucide-react";

import type { Calendar } from "@cohesive/api-entities";
import {
  BaseEntityCard,
  BaseEntityCardActions,
  BaseEntityCardBody,
  BaseEntityCardContent,
  BaseEntityCardContextMenu,
  BaseEntityCardDescription,
  BaseEntityCardExternalLinkAction,
  BaseEntityCardHeader,
  BaseEntityCardIcon,
  BaseEntityCardMentionAction,
  BaseEntityCardProps,
  BaseEntityCardTags,
  BaseEntityCardTitle,
} from "../BaseCard";

export { Calendar };

type CalendarCardProps = Calendar & BaseEntityCardProps;

const AccessRoleIcon = ({ role }: { role?: string }) => {
  switch (role?.toLowerCase()) {
    case "owner":
      return <Star className="h-3 w-3" />;
    case "writer":
      return <Pencil className="h-3 w-3" />;
    case "reader":
      return <Eye className="h-3 w-3" />;
    default:
      return null;
  }
};

// Avatar component for CalendarCard
export function CalendarCardAvatar({
  className,
  style,
}: Partial<CalendarCardProps>) {
  return (
    <BaseEntityCardIcon className={className} style={style}>
      <CalendarDays className="h-5 w-5 text-muted-foreground" />
    </BaseEntityCardIcon>
  );
}

export function CalendarCard({
  summary,
  description,
  primary,
  access_role,
  web_link,
  onClick,
  onDoubleClick,
  onMentionClick,
  actions,
  processes,
  className,
  style,
}: CalendarCardProps) {
  const descriptionPreview =
    description && description.length > 100
      ? `${description.substring(0, 100)}...`
      : description;

  return (
    <BaseEntityCard
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      processes={processes}
      className={className}
      style={style}
    >
      <CalendarCardAvatar />

      <BaseEntityCardContent>
        <BaseEntityCardHeader>
          <BaseEntityCardTitle
            platformIcon={
              primary ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Star className="inline h-4 w-4 mr-1.5 text-gold fill-yellow-400 align-middle" />
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Primary Calendar</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ) : undefined
            }
          >
            {summary}
          </BaseEntityCardTitle>
          <BaseEntityCardActions>
            {onMentionClick && (
              <BaseEntityCardMentionAction onClick={onMentionClick} />
            )}
            {web_link && <BaseEntityCardExternalLinkAction href={web_link} />}
            {actions && actions.length > 0 && (
              <BaseEntityCardContextMenu actions={actions} />
            )}
          </BaseEntityCardActions>
        </BaseEntityCardHeader>

        <BaseEntityCardBody>
          {descriptionPreview && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <BaseEntityCardDescription
                    className={cn("cursor-default", {
                      "line-clamp-2": description && description.length > 100,
                    })}
                  >
                    {descriptionPreview}
                  </BaseEntityCardDescription>
                </TooltipTrigger>
                {description && description.length > 100 && (
                  <TooltipContent className="max-w-xs break-words">
                    <p>{description}</p>
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>
          )}

          <BaseEntityCardTags>
            {access_role && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="outline" className="text-xs">
                      <AccessRoleIcon role={access_role} />
                      <span className="ml-1">{access_role}</span>
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Access Role: {access_role}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </BaseEntityCardTags>
        </BaseEntityCardBody>
      </BaseEntityCardContent>
    </BaseEntityCard>
  );
}
